import React, { FC, useEffect } from 'react';
import { Box } from '@mui/material';

import { MealOptionType } from '../../utils';

import { DialogHeader } from './DialogHeader';
import { DishImage } from './DishImage';
import { ContentSections } from './ContentSections';
import { DialogFooter } from './DialogFooter';
import { DishData, MealKitData } from './types';
import { scrollbarStyle } from './styles';

export interface DishInformationV2Props {
  data: DishData | MealKitData;
  mealProvisionType: MealOptionType;
  selectedCount: number;
  requiredCount: number;
  onAddToCart: () => void;
  onClose: () => void;
  isPreviewMode?: boolean;
  currentMealKitVariantId?: string;
  maxQuantity?: number;
  isEntree: boolean;
  onVariantChange: (variantId: string) => void;
  showVariantError: boolean;
  quantity?: number;
  onQuantityChange: (quantity: number) => void;
  v2?: boolean;
}

export const DishInformationV2: FC<DishInformationV2Props> = ({
  data,
  mealProvisionType,
  selectedCount = 0,
  requiredCount = 1,
  onAddToCart,
  onClose,
  isPreviewMode = false,
  currentMealKitVariantId,
  maxQuantity = 100,
  isEntree,
  onVariantChange,
  showVariantError,
  quantity = 0,
  onQuantityChange,
  v2 = false,
}) => {
  // Handle scrolling when variant error is shown
  useEffect(() => {
    if (showVariantError) {
      const ingredientsSection = document.querySelector(
        '[data-testid="ingredients-section"]'
      );
      if (ingredientsSection) {
        ingredientsSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [showVariantError]);

  const isMealKit = mealProvisionType === MealOptionType.MealKit;
  // Calculate the current total selected count including the current quantity
  const currentSelectedCount = selectedCount + quantity;

  return (
    <Box
      bgcolor="background.paper"
      width="100%"
      display="flex"
      flexDirection="column"
      overflow={isPreviewMode ? 'hidden' : 'auto'}
    >
      <DialogHeader title={data.name} onClose={onClose} />

      {/* Main Dialog Content */}
      <Box display="flex" height="600px">
        <DishImage src={data.photoUrl} alt={data.name} />

        {/* Right side - Content */}
        <Box
          width="50%"
          display="flex"
          flexDirection="column"
          bgcolor="background.default"
        >
          {/* Scrollable content area */}
          <Box flex={1} overflow="auto" sx={scrollbarStyle}>
            <ContentSections
              data={data}
              isMealKit={isMealKit}
              selectedVariantId={currentMealKitVariantId || ''}
              onVariantChange={onVariantChange}
              showVariantError={showVariantError}
              isPreviewMode={isPreviewMode}
              v2={v2}
            />
          </Box>
        </Box>
      </Box>

      {/* Footer - Only show if not in preview mode */}

      {!isPreviewMode && (
        <DialogFooter
          selectedCount={currentSelectedCount}
          requiredCount={requiredCount}
          quantity={quantity}
          onQuantityChange={onQuantityChange}
          onAddToCart={onAddToCart}
          isMealKit={isMealKit}
          maxQuantity={maxQuantity}
          isEntree={isEntree}
        />
      )}
    </Box>
  );
};

export default DishInformationV2;
